var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { Linking } from 'react-native';
import { injectable } from '../../../Data/Decorators/Injectable';
import { inject } from '../../../Data/Decorators/Inject';
import { IPushNotificationsRepositoryType } from '../../Interfaces/IPushNotificationsRepository';
import { IAuthRepositoryType } from '../../UseCases/Auth/IAuthRepository';
import { IDebugRepositoryType } from '../../UseCases/Application/IDebugRepository';
import { ICoordinatorServiceType } from '../CoordinatorService/ICoordinatorService';
import { IUserApplicationRepositoryType } from '../../UseCases/UserApplication/IUserApplicationRepository';
import { Observable } from '../../../Data/Observable/Observable';
import { whenAppIsActive } from '../../../Helpers/Background/Background';
let PushNotificationsService = class PushNotificationsService {
    constructor(authRepository, pushNotificationsRepository, debugRepository, coordinatorService, userApplicationRepository) {
        this.authRepository = authRepository;
        this.pushNotificationsRepository = pushNotificationsRepository;
        this.debugRepository = debugRepository;
        this.coordinatorService = coordinatorService;
        this.userApplicationRepository = userApplicationRepository;
        this.isEnabled = new Observable(false);
        this.pushNotificationsRepository.getPushNotifications().then(obs => {
            obs.observe(this, notifications => {
                if (notifications.length > 0) {
                    if (this.isEnabled.value) {
                        this.coordinatorService.navigateToPushNotification(notifications[notifications.length - 1].id);
                    }
                    else {
                        this.isEnabled.when(isEnabled => isEnabled).then(() => {
                            this.coordinatorService.navigateToPushNotification(notifications[notifications.length - 1].id);
                        });
                    }
                }
            });
        });
    }
    allowPushNotifications() {
        return this.pushNotificationsRepository.getHasPermissions()
            .then(has_persmission => {
            if (has_persmission) {
                return true;
            }
            return this.pushNotificationsRepository.requestPermissions();
        })
            .then(has_permission => {
            if (!has_permission) {
                return new Promise((res, rej) => {
                    this.coordinatorService.showPopUp({
                        title: 'Notifications',
                        description: 'Notifications permissions are missing! Please go to settings and enable them!',
                        interactions: [
                            {
                                type: 'tertiary',
                                text: 'Cancel',
                                action: 'cancel',
                                onPress: () => {
                                    this.coordinatorService.hidePopUp();
                                    rej(new Error('No notifications permissions!'));
                                }
                            },
                            {
                                type: 'primary',
                                text: 'Settings',
                                action: 'settings',
                                onPress: () => {
                                    this.coordinatorService.hidePopUp();
                                    Linking.openSettings()
                                        .then(whenAppIsActive)
                                        .then(() => this.pushNotificationsRepository.requestPermissions())
                                        .then(result => {
                                        if (!result) {
                                            return Promise.reject(new Error('No notifications permissions!'));
                                        }
                                        return result;
                                    })
                                        .then(res)
                                        .catch(rej);
                                }
                            }
                        ]
                    });
                });
            }
            return true;
        })
            .then(() => this.startPushNotifications().then(val => val.value))
            .then(isSuccessful => this.coordinatorService.hideNotificationsPermissions(isSuccessful))
            .then(() => undefined);
    }
    disabledPushNotifications() {
        return this.pushNotificationsRepository.disablePushNotifications();
    }
    denyPushNotifications() {
        return Promise.resolve()
            .then(() => this.coordinatorService.hideNotificationsPermissions(false))
            .then(() => undefined);
    }
    reloadNotifications() {
        this.pushNotificationsRepository.getLastPushNotification();
        return this.pushNotificationsRepository.getPushNotifications();
    }
    getHasNotificationPermissions() {
        return this.pushNotificationsRepository.getHasPermissions();
    }
    startPushNotifications() {
        if (!this.pushNotificationsRepository.getIsSupportedPushNotifications()) {
            this.debugRepository.send({
                type: 'push_notifications_error',
                data: {
                    message: 'Push notifications are supported only on mobile apps on physical devices!'
                }
            });
            if (!this.authRepository.getSID().value) {
                return Promise.resolve(this.isEnabled);
            }
            return this.userApplicationRepository.updateUserApplication().then(() => this.isEnabled);
        }
        if (!this.authRepository.getSID().value) {
            return Promise.resolve(this.isEnabled);
        }
        return this.pushNotificationsRepository.getHasPermissions()
            .then(hasPermissions => {
            if (!hasPermissions) {
                return Promise.reject(new Error('No push notifications permission!'));
            }
            return this.pushNotificationsRepository.setupChannels();
        })
            .then(() => {
            this.pushNotificationsRepository.getPushToken()
                .then(token => {
                if (!this.authRepository.getSID().value) {
                    this.isEnabled.next(false);
                    return;
                }
                return this.userApplicationRepository.updatePushToken(token);
            })
                .catch(error => {
                this.debugRepository.send({
                    type: 'push_notifications_error',
                    data: error
                });
            });
            return this.isEnabled.next(true);
        });
    }
    stopPushNotifications() {
        if (!this.authRepository.getSID().value) {
            return Promise.resolve(this.isEnabled.next(false));
        }
        return this.userApplicationRepository.updatePushToken('')
            .then(() => this.isEnabled.next(false));
    }
    getPushToken() {
        return this.pushNotificationsRepository.getPushToken();
    }
    setPushNotificationMiddleware(fn) {
        return this.pushNotificationsRepository.setPushNotificationMiddleware(fn);
    }
};
PushNotificationsService = __decorate([
    injectable({ type: 'singleton' }),
    __param(0, inject(IAuthRepositoryType)),
    __param(1, inject(IPushNotificationsRepositoryType)),
    __param(2, inject(IDebugRepositoryType)),
    __param(3, inject(ICoordinatorServiceType)),
    __param(4, inject(IUserApplicationRepositoryType)),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object])
], PushNotificationsService);
export { PushNotificationsService };
